import { useCallback, useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Lock } from "lucide-react";
import { Switch } from "@app/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@app/components/ui/tooltip";
import useFieldVisibility, {
  resolveFieldVisible,
} from "@app/zustand/field-visibility";
import { useFieldVisibilityEditable } from "@app/utils/use-field-visibility-editable";
import { BASE_IDENTITY_FIELD_KEYS } from "@app/utils/patient-field-groups";

export interface VisibilityField {
  field_key: string;
  name: string;
  /** Speciality default. Tri-state: null = inferred, true = forced on, false = hidden. */
  show_in_dashboard?: boolean | null;
  /** Speciality default. Null means visible; false hides it from the patient record. */
  show_on_patient_page?: boolean | null;
}

export interface VisibilityGroup {
  group: string;
  fields: VisibilityField[];
}

interface OntologyFieldVisibilityPanelProps {
  groups: VisibilityGroup[];
  /** Human label for a group, resolved by the caller (it owns the catalogs). */
  groupLabel: (group: string) => string;
  /** Human label for a field, resolved by the caller. */
  fieldLabel: (field: VisibilityField) => string;
}

const IDENTITY_KEYS = new Set<string>(BASE_IDENTITY_FIELD_KEYS);

/**
 * Lets a practitioner choose which extracted data THEIR screens show.
 *
 * The choice is personal: it is stored per user (``UserFieldVisibility``) and never
 * touches the ontology row, so two doctors of the same speciality curate different views
 * of the same data. This is the whole point of the panel — an earlier version wrote the
 * speciality row and silently reconfigured every colleague.
 *
 * ONE switch per field, driving both surfaces at once: a field switched off disappears
 * from the dashboard AND from the patient record. Two separate switches were offered first
 * and read as configuration work — the question a practitioner actually answers is "do I
 * care about this field?", not "on which of the two screens".
 *
 * Display only. The platform keeps extracting, aggregating and exporting every field
 * whatever is selected here, which is what makes switching one back on instantaneous
 * instead of a re-extraction.
 *
 * Off writes an explicit ``false`` for this user; on DELETES the override rather than
 * writing ``true``, so the field goes back to following the speciality default and a later
 * change to that default still reaches this practitioner.
 *
 * Identity data carries no switch — not even a disabled one. It is protected structurally
 * (separate table, patient header reads it directly, the API refuses to hide it), so
 * offering a control there would advertise a choice the product does not have; a lock
 * badge states the rule instead.
 */
export default function OntologyFieldVisibilityPanel({
  groups,
  groupLabel,
  fieldLabel,
}: OntologyFieldVisibilityPanelProps) {
  const { t } = useLingui();
  const canEdit = useFieldVisibilityEditable();
  const overrides = useFieldVisibility((s) => s.overrides);
  const setOverride = useFieldVisibility((s) => s.setOverride);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const patch = useCallback(
    async (fieldKey: string, visible: boolean) => {
      setPending((p) => ({ ...p, [fieldKey]: true }));
      setError(null);
      // Switching ON clears the override instead of storing `true`: the field returns to
      // the speciality default rather than being pinned to today's value of it.
      const ok = await setOverride(fieldKey, visible ? null : false);
      if (!ok) setError(t`La modification n'a pas pu être enregistrée.`);
      setPending((p) => {
        const next = { ...p };
        delete next[fieldKey];
        return next;
      });
    },
    [setOverride, t],
  );

  const rows = useMemo(
    () => groups.filter((g) => (g.fields ?? []).length > 0),
    [groups],
  );

  if (rows.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        <Trans>Aucune ontologie n'est configurée pour votre spécialité.</Trans>
      </p>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
        )}

        {canEdit && (
          <div className="grid grid-cols-[1fr_auto] items-center gap-x-6 px-4 text-2xs font-semibold uppercase tracking-wider text-slate-400">
            <span />
            <span className="w-24 text-center">
              <Trans>Affiché</Trans>
            </span>
          </div>
        )}

        {rows.map((group) => (
          <div key={group.group}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              {groupLabel(group.group)}
            </h3>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {group.fields.map((field) => {
                const locked = IDENTITY_KEYS.has(field.field_key);
                // The speciality default only ever hides: NULL means "auto" on the
                // dashboard and "visible" on the record, so a single explicit false on
                // either surface reads as off. The personal override then wins over it.
                const specialityDefault =
                  field.show_in_dashboard !== false &&
                  field.show_on_patient_page !== false;
                const visible = resolveFieldVisible(
                  field.field_key,
                  overrides,
                  specialityDefault,
                );
                const busy = pending[field.field_key];

                return (
                  <div
                    key={field.field_key ?? field.name}
                    className="grid grid-cols-[1fr_auto] items-center gap-x-6 px-4 py-2.5"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm text-slate-800">
                        {fieldLabel(field)}
                      </span>
                    </span>
                    {/* Identity data gets no switch at all: a disabled one still offers a
                        choice that does not exist — the API refuses to hide these fields
                        and the patient page rebuilds them from the identity floor. The
                        lock states the rule instead. */}
                    {canEdit && (
                      <span className="flex w-24 justify-center">
                        {locked ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 text-2xs text-slate-400">
                                <Lock className="h-3.5 w-3.5" />
                                <Trans>Toujours</Trans>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <Trans>Donnée d'identité — toujours affichée</Trans>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Switch
                            checked={visible}
                            disabled={busy}
                            onCheckedChange={(checked) =>
                              patch(field.field_key, checked)
                            }
                            aria-label={t`Afficher cette donnée`}
                          />
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {canEdit && (
          <p className="text-xs leading-relaxed text-slate-400">
            <Trans>
              Ce réglage vous est propre : il ne change que votre affichage, jamais
              celui de vos confrères. Une donnée désactivée disparaît du tableau de
              bord et de la fiche patient, mais reste extraite et conservée — la
              réactiver la fait réapparaître immédiatement.
            </Trans>
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}
